import { TicketStatus } from '../../shared/api/types/GraphQL';
import { NotificationSeenState } from '../../shared/utils/notification-state-storage.util';

/**
 * Generic notification row model (NBO-2331, design canon §2).
 *
 * Deliberately shaped so future kinds slot in without touching the panel:
 * icon + title + meta + time, plus the two-tier unread/unseen flags and a day
 * group. The panel renders whatever this returns and knows nothing about
 * orders or actions.
 */
export type NotificationKind = 'assigned' | 'status' | 'import' | 'mention';

export interface AppNotification {
  id: string;
  kind: NotificationKind;
  /** Already-resolved display strings — the source decides localization. */
  title: string;
  meta: string;
  time: string;
  /** Keeps the row's brand edge until the row itself is read. */
  unread: boolean;
  /** Feeds the badge; cleared for everything when the panel opens. */
  unseen: boolean;
  group: 'today' | 'earlier';
  /**
   * The ticket's own status. The bell does not render it — its rows are
   * all open work by definition — but the Home action queue shows it as a
   * pill (NBO-2390), and both surfaces share this one poll.
   */
  status: TicketStatus | null;
}

/** The slim ticket shape the NotificationTickets poll selects (NBO-2207). */
export interface NotificationTicketRow {
  id: string;
  titles?: { language?: string | null; value?: string | null }[] | null;
  createdAt?: string | null;
  type?: string | null;
  status?: TicketStatus | null;
}

/** Localization hooks so the derivation stays pure and spec-testable. */
export interface NotificationRowContext {
  /** Resolves a LocalizedString list in the active content language. */
  resolveTitle: (titles: NotificationTicketRow['titles']) => string;
  /** ngx-translate lookup for the meta line and time labels. */
  translate: (key: string, params?: Record<string, unknown>) => string;
  now: Date;
}

/**
 * Derives panel rows from the polled assigned tickets + the persisted
 * seen/read id sets. Detection is by ID DIFF, not timestamps: the schema has
 * no assignedAt, createdAt misses a ticket assigned after creation, and
 * lastModifiedAt bumps on any edit — so "new" simply means "an assigned open
 * ticket whose id this user has not seen yet".
 */
export function buildNotificationRows(
  tickets: NotificationTicketRow[],
  state: NotificationSeenState,
  ctx: NotificationRowContext
): AppNotification[] {
  const seen = new Set(state.seenIds);
  const read = new Set(state.readIds);

  return tickets.map((ticket) => {
    const unread = !read.has(ticket.id);
    return {
      id: ticket.id,
      kind: 'assigned' as const,
      title: ctx.resolveTitle(ticket.titles) || ctx.translate('nav.topbar.notification_untitled'),
      meta: ctx.translate('nav.topbar.notification_assigned_meta'),
      time: relativeTimeLabel(ticket.createdAt, ctx),
      unread,
      unseen: unread && !seen.has(ticket.id),
      group: isToday(ticket.createdAt, ctx.now) ? ('today' as const) : ('earlier' as const),
      status: ticket.status ?? null
    };
  });
}

function isToday(createdAt: string | null | undefined, now: Date): boolean {
  if (!createdAt) return false;
  const created = new Date(createdAt);
  return (
    created.getFullYear() === now.getFullYear() &&
    created.getMonth() === now.getMonth() &&
    created.getDate() === now.getDate()
  );
}

/**
 * Compact relative age ("now", "5m", "3h", "2d") — re-derived on every poll
 * tick, so it stays at most ~60s stale without its own timer.
 */
function relativeTimeLabel(createdAt: string | null | undefined, ctx: NotificationRowContext): string {
  if (!createdAt) return '';
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return '';

  const elapsedMs = Math.max(0, ctx.now.getTime() - created);
  const minutes = Math.floor(elapsedMs / 60_000);
  if (minutes < 1) return ctx.translate('nav.topbar.time_now');
  if (minutes < 60) return ctx.translate('nav.topbar.time_minutes_short', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return ctx.translate('nav.topbar.time_hours_short', { count: hours });
  const days = Math.floor(hours / 24);
  return ctx.translate('nav.topbar.time_days_short', { count: days });
}
